import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { MDText } from 'i18n-react';

import { toast } from '@motorway/mw-highway-code/pre-alpha';

import { useToggleSavedFilter } from 'Components/SavedSearch/useToggleSavedFilter';
import { useFilterCountPreviewImperative } from 'Components/VehicleList/VehicleListV2/FilterBarV2/hooks/useFilterCountPreview';
import texts from 'Components/VehicleList/VehicleListV2/VehicleListV2.texts.json';
import {
	triggerAddSavedSearchDailyStockToggleEvent,
	triggerDeleteSavedSearchLinkClickedEvent,
	triggerManageSavedSearchDailyStockToggleEvent,
	triggerSavedSearchDeleteButtonClickEvent,
	triggerSaveFilterButtonClickedEvent,
	triggerUserClickedShowVehiclesEvent,
} from 'Services/analytics/events';
import {
	useFilters,
	useIsVehicleCountLoading,
	useResetFilters,
	useVehicleCount,
} from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import useSavedFiltersStore, {
	useGetSavedFilter,
	useSavedFilters,
	useSetCurrentFilter,
} from 'Stores/SavedFiltersStore/SavedFiltersStore';
import type { Filter } from 'Types/filter';
import type { SavedFilter } from 'Types/savedFilter';
import useFormatSavedFilterValues from 'Utilities/hooks/useFormatSavedFilterValues';
import usePrevious from 'Utilities/hooks/usePrevious';

import type { NameError } from './validateSavedSearchName';
import { validateSavedSearchName } from './validateSavedSearchName';

const LocalT = new MDText(texts.savedSearchModals);

export type ModalView = 'closed' | 'create' | 'delete' | 'edit' | 'list';

type ViewAction =
	| { filter: SavedFilter; type: 'START_EDIT' }
	| { selectedId?: number; type: 'OPEN' }
	| { type: 'CLOSE' }
	| { type: 'CONFIRM_DELETE' }
	| { type: 'GO_BACK_TO_EDIT' }
	| { type: 'GO_BACK_TO_LIST' }
	| { type: 'START_CREATE' }
	| { type: 'START_DELETE' };

interface ViewState {
	selectedId?: number;
	view: ModalView;
}

const viewReducer = (state: ViewState, action: ViewAction): ViewState => {
	switch (action.type) {
		case 'CLOSE':
			return { selectedId: undefined, view: 'closed' };
		case 'CONFIRM_DELETE':
			return { selectedId: undefined, view: 'closed' };
		case 'GO_BACK_TO_EDIT':
			return { ...state, view: 'edit' };
		case 'GO_BACK_TO_LIST':
			return { ...state, view: 'list' };
		case 'OPEN':
			return { selectedId: action.selectedId, view: 'list' };
		case 'START_CREATE':
			return { selectedId: undefined, view: 'create' };
		case 'START_DELETE':
			return { ...state, view: 'delete' };
		case 'START_EDIT':
			return { selectedId: action.filter.id, view: 'edit' };
		default:
			return state;
	}
};

interface UseSavedSearchesFlowArgs {
	activeFilterId?: number;
	initialView?: 'create' | 'list';
	isOpen: boolean;
	onClose: () => void;
}

type SavedFilterValues = ReturnType<typeof useFormatSavedFilterValues>;

export interface ListViewModel {
	activeFilterId?: number;
	isCountLoading: boolean;
	onClearFilters: () => void;
	onEditRow: (id: number) => void;
	onSelect: (id: number) => void;
	onShowVehicles: () => void;
	previewCount: number;
	savedFilters: SavedFilter[];
	selectedId?: number;
}

export interface CreateViewModel {
	isSaving: boolean;
	name: string;
	nameError: Nullable<NameError>;
	onNameChange: (value: string) => void;
	onSave: () => void;
	onToggleEmailAlert: () => void;
	savedFilterValues: SavedFilterValues;
	sendEmailAlert: boolean;
}

export interface EditViewModel {
	editName: string;
	handleBackButtonClick: () => void;
	isSaving: boolean;
	isSendEmailAlert: boolean;
	nameError: Nullable<NameError>;
	onDelete: () => void;
	onNameChange: (value: string) => void;
	onSave: () => void;
	onToggleEmailAlert: () => void;
	savedFilterValues: SavedFilterValues;
	selectedFilter?: SavedFilter;
}

export interface DeleteViewModel {
	isSaving: boolean;
	onCancel: () => void;
	onConfirm: () => void;
	savedFilterValues: SavedFilterValues;
	selectedFilter?: SavedFilter;
}

export interface UseSavedSearchesFlow {
	create: CreateViewModel;
	delete: DeleteViewModel;
	edit: EditViewModel;
	list: ListViewModel;
	onClose: () => void;
	view: ModalView;
}

export const useSavedSearchesFlow = ({
	activeFilterId,
	initialView = 'list',
	isOpen,
	onClose,
}: UseSavedSearchesFlowArgs): UseSavedSearchesFlow => {
	const filters = useFilters() as Filter;
	const savedFilters = useSavedFilters() as SavedFilter[];
	const vehicleCount = useVehicleCount();
	const isVehicleCountLoading = useIsVehicleCountLoading();
	const toggleSavedFilter = useToggleSavedFilter();
	const getSavedFilter = useGetSavedFilter();
	const createSavedFilter = useSavedFiltersStore((store) => store.createSavedFilter);
	const updateSavedFilter = useSavedFiltersStore((store) => store.updateSavedFilter);
	const deleteSavedFilter = useSavedFiltersStore((store) => store.deleteSavedFilter);
	const resetFilters = useResetFilters();
	const setCurrentFilter = useSetCurrentFilter();

	const initialState: ViewState = (() => {
		if (!isOpen) return { selectedId: undefined, view: 'closed' as const };
		if (initialView === 'create') return { selectedId: undefined, view: 'create' as const };
		return { selectedId: activeFilterId, view: 'list' as const };
	})();

	const [{ selectedId, view }, dispatch] = useReducer(viewReducer, initialState);

	const [formName, setFormName] = useState('');
	const [isSendEmailAlert, setIsSendEmailAlert] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const {
		count: fetchedCount,
		fetchCount,
		isFetching: isPreviewFetching,
		reset: resetCount,
	} = useFilterCountPreviewImperative();

	const selectedFilter = selectedId ? getSavedFilter(selectedId) : undefined;

	const selectedFilterValues = useFormatSavedFilterValues({
		filterData: selectedFilter?.filterData,
		limitArrayValues: true,
	});

	const currentFilterValues = useFormatSavedFilterValues({
		filterData: filters,
		limitArrayValues: true,
	});

	const formNameError = useMemo(
		() =>
			validateSavedSearchName({
				excludeName: view === 'edit' ? selectedFilter?.name : undefined,
				name: formName,
				savedFilters,
			}),
		[formName, savedFilters, selectedFilter?.name, view],
	);

	const previewCount = fetchedCount ?? vehicleCount;
	const isCountLoading =
		isPreviewFetching || (selectedId !== undefined && fetchedCount === undefined && isVehicleCountLoading);

	const handleClose = useCallback(() => {
		resetCount();
		setFormName('');
		setIsSendEmailAlert(false);
		dispatch({ type: 'CLOSE' });
		onClose();
	}, [onClose, resetCount]);

	const handleNameChange = useCallback((value: string) => setFormName(value), []);
	const handleToggleEmailAlert = useCallback(() => setIsSendEmailAlert((prev) => !prev), []);

	const handleSelect = useCallback(
		(id: number) => {
			if (selectedId === id) {
				resetCount();
				dispatch({ selectedId: activeFilterId, type: 'OPEN' });
				return;
			}
			dispatch({ selectedId: id, type: 'OPEN' });
			const filter = savedFilters.find((f) => f.id === id);
			if (filter) {
				fetchCount(filter.filterData as unknown as Partial<Filter>);
			}
		},
		[activeFilterId, fetchCount, resetCount, savedFilters, selectedId],
	);

	const handleShowVehicles = useCallback(() => {
		if (selectedId && selectedId !== activeFilterId) {
			toggleSavedFilter(selectedId);
		}
		triggerUserClickedShowVehiclesEvent();
		handleClose();
	}, [activeFilterId, handleClose, selectedId, toggleSavedFilter]);

	const handleClearFilters = useCallback(() => {
		resetFilters();
		setCurrentFilter(undefined);
		handleClose();
	}, [handleClose, resetFilters, setCurrentFilter]);

	const handleEditRow = useCallback(
		(id: number) => {
			const filter = getSavedFilter(id);
			if (filter) {
				setFormName(filter.name);
				setIsSendEmailAlert(filter.sendEmailAlert);
				dispatch({ filter, type: 'START_EDIT' });
			}
		},
		[getSavedFilter],
	);

	const handleCreate = useCallback(async () => {
		if (!formName.trim() || formNameError) return;

		setIsSaving(true);
		triggerSaveFilterButtonClickedEvent();
		triggerAddSavedSearchDailyStockToggleEvent({ savedSearchName: formName, sendEmailAlert: isSendEmailAlert });

		const result = await createSavedFilter({
			filterData: filters,
			name: formName.trim(),
			sendEmailAlert: isSendEmailAlert,
		});
		setIsSaving(false);

		if (!result) return;

		toast({ content: `${LocalT.translate('createSuccess')}`, variant: 'success' });
		handleClose();
	}, [createSavedFilter, filters, formName, formNameError, handleClose, isSendEmailAlert]);

	const handleSaveEdit = useCallback(async () => {
		if (!formName.trim() || formNameError || !selectedFilter) return;

		setIsSaving(true);
		triggerManageSavedSearchDailyStockToggleEvent({ id: selectedFilter.id, sendEmailAlert: isSendEmailAlert });

		const result = await updateSavedFilter({
			filterData: selectedFilter.filterData,
			id: selectedFilter.id,
			name: formName.trim(),
			sendEmailAlert: isSendEmailAlert,
		});
		setIsSaving(false);

		if (!result) return;

		dispatch({ type: 'GO_BACK_TO_LIST' });
	}, [formName, formNameError, isSendEmailAlert, selectedFilter, updateSavedFilter]);

	const handleDeleteFromEdit = useCallback(() => {
		if (!selectedFilter) return;
		triggerDeleteSavedSearchLinkClickedEvent(selectedFilter.id);
		dispatch({ type: 'START_DELETE' });
	}, [selectedFilter]);

	const handleConfirmDelete = useCallback(async () => {
		if (!selectedFilter) return;

		setIsSaving(true);
		triggerSavedSearchDeleteButtonClickEvent(selectedFilter.id);
		const result = await deleteSavedFilter(selectedFilter.id);
		setIsSaving(false);

		if (!result) return;

		if (selectedFilter.id === activeFilterId) {
			resetFilters();
			setCurrentFilter(undefined);
		}

		toast({ content: `${LocalT.translate('deleteSuccess')}`, variant: 'success' });
		dispatch({ type: 'CONFIRM_DELETE' });
		onClose();
	}, [activeFilterId, deleteSavedFilter, onClose, resetFilters, selectedFilter, setCurrentFilter]);

	const handleCancelDelete = useCallback(() => dispatch({ type: 'GO_BACK_TO_EDIT' }), []);

	const handleBackButtonClick = useCallback(() => dispatch({ type: 'GO_BACK_TO_LIST' }), []);

	const prevIsOpen = usePrevious(isOpen);

	useEffect(() => {
		if (isOpen && !prevIsOpen) {
			if (initialView === 'create') {
				setFormName('');
				setIsSendEmailAlert(false);
				dispatch({ type: 'START_CREATE' });
			} else {
				dispatch({ selectedId: activeFilterId, type: 'OPEN' });
			}
		} else if (!isOpen && prevIsOpen) {
			resetCount();
			dispatch({ type: 'CLOSE' });
		}
	}, [activeFilterId, initialView, isOpen, prevIsOpen, resetCount]);

	return {
		create: {
			isSaving,
			name: formName,
			nameError: formNameError,
			onNameChange: handleNameChange,
			onSave: handleCreate,
			onToggleEmailAlert: handleToggleEmailAlert,
			savedFilterValues: currentFilterValues,
			sendEmailAlert: isSendEmailAlert,
		},
		delete: {
			isSaving,
			onCancel: handleCancelDelete,
			onConfirm: handleConfirmDelete,
			savedFilterValues: selectedFilterValues,
			selectedFilter,
		},
		edit: {
			editName: formName,
			handleBackButtonClick,
			isSaving,
			isSendEmailAlert,
			nameError: formNameError,
			onDelete: handleDeleteFromEdit,
			onNameChange: handleNameChange,
			onSave: handleSaveEdit,
			onToggleEmailAlert: handleToggleEmailAlert,
			savedFilterValues: selectedFilterValues,
			selectedFilter,
		},
		list: {
			activeFilterId,
			isCountLoading,
			onClearFilters: handleClearFilters,
			onEditRow: handleEditRow,
			onSelect: handleSelect,
			onShowVehicles: handleShowVehicles,
			previewCount,
			savedFilters,
			selectedId,
		},
		onClose: handleClose,
		view,
	};
};
